/** @jsx jsx */
import { useEffect, useMemo } from 'react';
import { jsx } from '@emotion/core';
import { Box, Container } from '@material-ui/core';
import { propEq, find } from 'ramda';

import CardSelectorWidget from './CardSelectorWidget';
import useTranslate, {
  useMarkdownTranslate,
} from 'hooks/useTranslate/useTranslate';
import { Form, FormikProps, FieldProps, Field } from 'formik';
import PageContainer from 'components/PageContainer';
import Title from 'components/Title';
import Button from 'components/Button';
import BackToPrevious from 'components/unified/BackToPrevious';
import ActionButtonContainer from 'components/ActionButtonsContainer';

import { CardSelector } from 'state/tenant';
import styles from './styles';
import ImageWithLoadSpinner from 'components/ImageWithLoadSpinner';

import { FormValues } from './CardSelector';
import { useDispatch } from 'react-redux';
import { setForceTenantLogo } from 'state/tenantLogo';

type PropTypes<T extends FormValues> = FormikProps<T> & {
  cardSelector: CardSelector;
};

export default function CardSelectorView<T extends FormValues>(
  props: PropTypes<T>,
) {
  const dispatch = useDispatch();
  const t = useTranslate();
  const tMd = useMarkdownTranslate();
  const { cardSelector, isValid, isSubmitting, values } = props;

  const selectedCard = useMemo(() => {
    return find(propEq('key', values.selectedCardKey), cardSelector.options);
  }, [cardSelector.options, values.selectedCardKey]);

  const defaultCard = useMemo(
    () => find(propEq('key', cardSelector.defaultKey), cardSelector.options),
    [cardSelector.defaultKey, cardSelector.options],
  );

  const imageUrl = useMemo(() => {
    const card = selectedCard || defaultCard;
    return card ? card.imageUrl : '';
  }, [defaultCard, selectedCard]);

  const name = useMemo(() => {
    const card = selectedCard || defaultCard;
    return card ? card.name : '';
  }, [defaultCard, selectedCard]);

  useEffect(() => {
    dispatch(setForceTenantLogo({ forceTenantLogo: true }));
    return () => {
      dispatch(setForceTenantLogo({ forceTenantLogo: false }));
    };
  }, [dispatch]);

  return (
    <Form>
      <Container maxWidth="sm">
        <PageContainer>
          <Box mb={1}>
            <Title css={styles.pageTitleText}>
              {tMd('cardSelector.title')}
            </Title>
          </Box>
          <Box mb={4} css={styles.normalText}>
            {tMd('cardSelector.subtitle')}
          </Box>

          <Box mb={5} css={styles.imageContainer}>
            <ImageWithLoadSpinner
              imgCss={styles.cardImage}
              src={imageUrl}
              alt={name}
            />
          </Box>

          <Box mb={5}>
            <Field name="selectedCardKey">
              {(fieldProps: FieldProps) => (
                <CardSelectorWidget
                  selectedCardKey={fieldProps.field.value}
                  onSelect={value =>
                    fieldProps.form.setFieldValue(fieldProps.field.name, value)
                  }
                />
              )}
            </Field>
          </Box>

          <ActionButtonContainer>
            <Button
              type="submit"
              id="cardSelectorSubmit"
              disabled={!isValid || isSubmitting}
              loading={isSubmitting}
              data-cy="continueButton"
            >
              {t('cardSelector.submitButton')}
            </Button>

            <BackToPrevious tMdKey="cardSelector.backButton" />
          </ActionButtonContainer>
        </PageContainer>
      </Container>
    </Form>
  );
}
